/**
 * DiagRunStatus: Diagnostics drawer -- run status tiles and allocation invariant violations, moved verbatim out of src/pages/ScreenView.tsx (Screener Phase 3, 2026-10-28). Display only.
 */
import React from "react";
import { MICRO_LABEL } from "@components/cockpit";
import { AnalyticsMetric, badgeStyle, mutedText, secondaryText, ui } from "@components/screener/screenerUi";
import { numberFmt, summarizeDataWarnings } from "@lib/screenerTruth";
import { allocationViolationDetail, allocationViolationTitle } from "@lib/screenerViewHelpers";
import { type AllocationValidationViolation, type DataConfidence, type DecisionContext } from "@lib/screenerTypes";

export type DiagRunStatusProps = {
  allocationValidationViolations: AllocationValidationViolation[];
  dataConfidence: DataConfidence | null;
  decisionContext: DecisionContext | null;
  mlWeight: number | null;
};

export default function DiagRunStatus({
  allocationValidationViolations,
  dataConfidence,
  decisionContext,
  mlWeight,
}: DiagRunStatusProps) {
  return (
    <div>
      <div className={`${MICRO_LABEL} mb-2`} style={mutedText}>Run status</div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <AnalyticsMetric
          label="Data confidence"
          value={String(dataConfidence?.status || decisionContext?.dataConfidence?.status || "—").toUpperCase()}
          subtext={
            (dataConfidence?.warnings?.length || decisionContext?.dataWarnings?.length)
              ? summarizeDataWarnings(dataConfidence?.warnings || decisionContext?.dataWarnings || [], { mlWeight })
              : "No data warnings reported."
          }
        />
        <AnalyticsMetric
          label="Decision context"
          value={String(decisionContext?.status || "—").toUpperCase()}
          subtext={decisionContext?.allowCioDiscretion === false ? "Hard block active." : "CIO retains final discretion."}
        />
        <AnalyticsMetric
          label="Selected quality"
          value={
            decisionContext?.selectedLineQuality
              ? decisionContext.selectedLineQuality.lowQualitySelectedCount
                ? "Mixed"
                : "Usable"
              : "—"
          }
          subtext={
            decisionContext?.selectedLineQuality
              ? `${decisionContext.selectedLineQuality.lowQualitySelectedCount || 0} low-score · min ${numberFmt(decisionContext.selectedLineQuality.minimumEntryScore, 3)} · ${decisionContext.selectedLineQuality.distinctExpiryCount || 0}/${decisionContext.selectedLineQuality.targetDistinctExpiryCount || "—"} expiries`
              : "Selected-line quality unavailable."
          }
        />
      </div>
      {allocationValidationViolations.length > 0 && (
        <div
          className="mt-4 rounded-2xl border p-3 text-sm"
          style={{
            borderColor: "rgba(239, 68, 68, 0.35)",
            backgroundColor: "rgba(239, 68, 68, 0.08)",
          }}
        >
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="font-semibold">Allocation invariant violations</div>
            <span style={badgeStyle("danger")}>Fail Review</span>
          </div>
          <div className="space-y-2">
            {allocationValidationViolations.map((violation, idx) => (
              <div
                key={`${violation.code || "violation"}-${idx}`}
                className="rounded-xl border px-3 py-2"
                style={{ borderColor: ui.cardBorder, backgroundColor: ui.cardBg }}
              >
                <div className="font-semibold">{allocationViolationTitle(violation.code)}</div>
                <div className="mt-1 text-xs" style={secondaryText}>
                  {allocationViolationDetail(violation)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
